// src/components/Admin/FaqEditor.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Upload,
  Send,
  Check,
  AlertCircle,
  RefreshCw,
  FileText,
  Users,
  Eye,
  EyeOff,
  Loader2,
} from "lucide-react";
import {
  listClients,
  updateFaqForAllClients,
} from "../../services/adminService";
import FileUploader from "./shared/FileUploader";
import { useToast } from "./shared/ToastContext";

const FaqEditor = () => {
  const toast = useToast();

  // FAQ file state
  const [faqContent, setFaqContent] = useState(null);
  const [faqFileName, setFaqFileName] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Client list for propagation
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);

  // Propagation state
  const [propagating, setPropagating] = useState(false);
  const [propagateResult, setPropagateResult] = useState(null);
  const [error, setError] = useState(null);

  // File object kept for propagation
  const [faqFile, setFaqFile] = useState(null);

  // Fetch clients on mount
  useEffect(() => {
    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        const data = await listClients();
        setClients(data);
      } catch (err) {
        console.error("Failed to load clients:", err);
      } finally {
        setLoadingClients(false);
      }
    };
    fetchClients();
  }, []);

  const activeClients = clients.filter((c) => c.status === "active");

  // Handle FAQ file selection (reads locally for preview — doesn't upload yet)
  const handleFileSelected = useCallback(async (file) => {
    setError(null);
    setPropagateResult(null);

    const text = await file.text();
    setFaqContent(text);
    setFaqFileName(file.name);
    setFaqFile(file);
    setShowPreview(true);
  }, []);

  // Propagate FAQ to all active clients
  const handlePropagate = async () => {
    if (!faqFile) {
      setError("No FAQ file loaded. Upload a file first.");
      return;
    }

    if (activeClients.length === 0) {
      setError("No active clients to propagate to.");
      return;
    }

    setPropagating(true);
    setError(null);
    setPropagateResult(null);

    try {
      const result = await updateFaqForAllClients(faqFile);
      setPropagateResult(result);
      const count = result.updated?.length || 0;
      toast.success(
        `FAQ propagated to ${count} client${count === 1 ? "" : "s"}`,
      );
    } catch (err) {
      setError(err.message || "Propagation failed");
      toast.error("FAQ propagation failed");
    } finally {
      setPropagating(false);
    }
  };

  // Simple markdown-to-HTML for preview
  const renderMarkdownPreview = (md) => {
    if (!md) return "";
    const escaped = md
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    return escaped
      .replace(
        /^### (.+)$/gm,
        '<h3 class="text-base font-semibold text-gray-800 mt-4 mb-1">$1</h3>',
      )
      .replace(
        /^## (.+)$/gm,
        '<h2 class="text-lg font-bold text-gray-800 mt-5 mb-2">$1</h2>',
      )
      .replace(
        /^# (.+)$/gm,
        '<h1 class="text-xl font-bold text-gray-900 mt-6 mb-2">$1</h1>',
      )
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(
        /^[-*] (.+)$/gm,
        '<li class="ml-4 text-sm text-gray-700">$1</li>',
      )
      .replace(
        /^\d+\. (.+)$/gm,
        '<li class="ml-4 text-sm text-gray-700 list-decimal">$1</li>',
      )
      .replace(/^---$/gm, '<hr class="my-4 border-gray-200" />')
      .replace(/\n\n/g, '<div class="mb-3"></div>')
      .replace(/\n/g, "<br />");
  };

  return (
    <div>
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">FAQ Manager</h1>
        <p className="text-sm text-gray-500 mt-1">
          Upload a new FAQ markdown file and propagate it to all active clients
        </p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-900">
            {loadingClients ? "—" : clients.length}
          </div>
          <div className="text-xs text-gray-500">Total Clients</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-2xl font-bold text-emerald-600">
            {loadingClients ? "—" : activeClients.length}
          </div>
          <div className="text-xs text-gray-500">Active Clients</div>
        </div>
        <div className="hidden sm:block bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-900">
            {faqFileName ? "1" : "0"}
          </div>
          <div className="text-xs text-gray-500">File Staged</div>
        </div>
      </div>

      {/* Upload section */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <div className="flex items-center gap-2.5 mb-4">
          <Upload size={18} className="text-gray-500" />
          <div>
            <h3 className="text-sm font-semibold text-gray-800">
              Upload FAQ File
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Select a markdown (.md) file to preview and propagate
            </p>
          </div>
        </div>

        <FileUploader
          onUpload={handleFileSelected}
          accept=".md"
          acceptLabel="Markdown files (.md)"
          maxSizeMB={5}
          currentFile={faqFileName}
        />
      </div>

      {/* Preview section */}
      {faqContent && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <FileText size={18} className="text-gray-500" />
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Preview</h3>
                <p className="text-xs text-gray-400 font-mono">{faqFileName}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg transition"
            >
              {showPreview ? (
                <>
                  <EyeOff size={14} />
                  Hide
                </>
              ) : (
                <>
                  <Eye size={14} />
                  Show
                </>
              )}
            </button>
          </div>

          {showPreview && (
            <div
              className="p-5 prose prose-sm max-w-none text-gray-700 overflow-auto max-h-96"
              dangerouslySetInnerHTML={{
                __html: renderMarkdownPreview(faqContent),
              }}
            />
          )}
        </div>
      )}

      {/* Propagation section */}
      {faqContent && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <div className="flex items-center gap-2.5 mb-4">
            <Users size={18} className="text-gray-500" />
            <div>
              <h3 className="text-sm font-semibold text-gray-800">
                Propagate to Clients
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Upload this FAQ to every active client's{" "}
                <span className="font-mono">/reports/faq.md</span>
              </p>
            </div>
          </div>

          {loadingClients ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
              <RefreshCw size={14} className="animate-spin" />
              Loading clients...
            </div>
          ) : activeClients.length === 0 ? (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-center gap-2">
              <AlertCircle size={16} />
              No active clients found. FAQ propagation requires at least one
              active client.
            </div>
          ) : (
            <>
              <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-xs text-gray-600 mb-2">
                  Will update{" "}
                  <span className="font-semibold">{activeClients.length}</span>{" "}
                  active {activeClients.length === 1 ? "client" : "clients"}:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {activeClients.map((c) => (
                    <span
                      key={c.id}
                      className="inline-flex items-center px-2 py-0.5 text-xs font-mono text-gray-600 bg-white border border-gray-200 rounded"
                    >
                      {c.id}
                    </span>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={handlePropagate}
                disabled={propagating}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary text-white text-sm font-medium rounded-lg hover:bg-secondary-dark transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {propagating ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
                {propagating
                  ? "Propagating..."
                  : `Propagate to ${activeClients.length} Client${activeClients.length === 1 ? "" : "s"}`}
              </button>
            </>
          )}
        </div>
      )}

      {/* Propagation result */}
      {propagateResult && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <div className="flex items-center gap-2.5 mb-3">
            <Check size={18} className="text-emerald-500" />
            <h3 className="text-sm font-semibold text-gray-800">
              Propagation Complete
            </h3>
          </div>

          {propagateResult.updated?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-medium text-emerald-700 mb-1">
                Updated ({propagateResult.updated.length}):
              </p>
              <div className="flex flex-wrap gap-1.5">
                {propagateResult.updated.map((id) => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono text-emerald-700 bg-emerald-50 rounded"
                  >
                    <Check size={10} />
                    {id}
                  </span>
                ))}
              </div>
            </div>
          )}

          {propagateResult.failed?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-red-700 mb-1">
                Failed ({propagateResult.failed.length}):
              </p>
              <div className="flex flex-wrap gap-1.5">
                {propagateResult.failed.map((id) => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono text-red-700 bg-red-50 rounded"
                  >
                    <AlertCircle size={10} />
                    {id}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <AlertCircle size={16} />
          {error}
        </div>
      )}
    </div>
  );
};

export default FaqEditor;
